import React, { Component } from 'react'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import ToggleButton from 'react-bootstrap/ToggleButton'
import ToggleButtonGroup from 'react-bootstrap/ToggleButtonGroup'
import Modal from 'react-bootstrap/Modal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faArrowRight, faTrash } from '@fortawesome/free-solid-svg-icons'
import Zoom from 'react-medium-image-zoom'
import randomColor from 'randomcolor'
import { v4 as uuidv4 } from 'uuid';

import './ImageEditor.css'

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: 'none',
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? 'lightgreen' : '#ccc',

    // styles we need to apply on draggables
    ...draggableStyle
});

const getListStyle = isDraggingOver => ({
    background: isDraggingOver ? 'lightblue' : '#e5e5e5',
    padding: grid,
    width: 250
});

class ImageEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      changed: false,
      updating: false,
      currentFilename: null,
      sentences: null,
      editingSentence: null,
      imageLoading: true,
      imageGuide: true,
      canvasURL: null,
    }
    this.displayRef = React.createRef();
    this.imageRef = React.createRef();
  }

  copyState() {
    const state = Object.assign({}, this.state)
    state.sentences = state.sentences === null ? null : state.sentences.map(s => Object.assign({}, s))
    return state
  }

  componentDidMount() {
    setTimeout(() => {
      this.setState({
        currentFilename: 0,
        sentences: JSON.parse(this.props.taskdata.data.sentences).map(sentence => Object.assign({
          color: randomColor({ alpha: 0.8 }),
        }, sentence)),
      })
    }, 100)
  }

  componentDidUpdate() {
    if (this.state.changed) {
      window.onbeforeunload = () => true
    } else {
      window.onbeforeunload = undefined
    }
  }

  pageChange(to) {
    this.setState({
      currentFilename: this.state.currentFilename + to,
    })
  }

  editSentence(sentenceId) {
    this.setState({
      editingSentence: sentenceId,
    })
  }

  closeEditSentence() {
    this.setState({
      editingSentence: null,
    })
  }

  sentenceTextChange(ev) {
    const state = this.copyState()
    const sentences = state.sentences.filter(s => s.id === state.editingSentence)
    if (sentences.length === 0) {
      return
    }
    sentences[0].text = ev.target.value
    sentences[0].fixedText = true
    state.changed = true
    this.setState(state)
  }

  locationChange(coord, ev) {
    const state = this.copyState()
    const sentences = state.sentences.filter(s => s.id === state.editingSentence)
    if (sentences.length === 0) {
      return
    }
    const location = {
      x1: Math.min(...sentences[0].locations.map(l => l.x1)),
      x2: Math.max(...sentences[0].locations.map(l => l.x2)),
      y1: Math.min(...sentences[0].locations.map(l => l.y1)),
      y2: Math.max(...sentences[0].locations.map(l => l.y2)),
    }
    location[coord] = parseInt(ev.target.value)
    sentences[0].locations = [location]
    sentences[0].fixedLocations = true
    state.changed = true
    this.setState(state, () => this.drawDisplay())
  }

  renderModal() {
    const sentences = (this.state.sentences || []).filter(s => s.id === this.state.editingSentence)
    return <Modal
      data-test-sentence-modal-detail
      show={sentences.length !== 0}
      animation={true}
      onHide={e => this.closeEditSentence()}>
      <Modal.Header closeButton>
        <Modal.Title>"{sentences[0] ? sentences[0].text : ''}" の編集</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group>
          <Form.Label>文</Form.Label>
          <Form.Control
            placeholder='文'
            defaultValue={sentences[0] ? sentences[0].text : ''}
            onChange={e => this.sentenceTextChange(e)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>X1</Form.Label>
          <Form.Control
            placeholder='X1'
            defaultValue={sentences[0] ? `${Math.min(...sentences[0].locations.map(l => l.x1))}` : ''}
            onChange={e => this.locationChange('x1', e)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>X2</Form.Label>
          <Form.Control
            placeholder='X2'
            defaultValue={sentences[0] ? `${Math.max(...sentences[0].locations.map(l => l.x2))}` : ''}
            onChange={e => this.locationChange('x2', e)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Y1</Form.Label>
          <Form.Control
            placeholder='Y1'
            defaultValue={sentences[0] ? `${Math.min(...sentences[0].locations.map(l => l.y1))}` : ''}
            onChange={e => this.locationChange('y1', e)} />
        </Form.Group>
        <Form.Group>
          <Form.Label>Y2</Form.Label>
          <Form.Control
            placeholder='Y2'
            defaultValue={sentences[0] ? `${Math.max(...sentences[0].locations.map(l => l.y2))}` : ''}
            onChange={e => this.locationChange('y2', e)} />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant='light'
          data-test-sentence-detail-close
          onClick={e => this.closeEditSentence(e)}>
          閉じる
        </Button>
      </Modal.Footer>
    </Modal>
  }

  renderPageControl() {
    if (this.state.currentFilename === null) {
      return <div></div>
    }
    return <div className='PageControl'>
      <Button
        variant='light'
        data-test-cell-prev
        disabled={this.state.hasPrevPage}
        onClick={e => this.pageChange(-1)}>
        前のページ
      </Button>
      {this.state.currentFilename} / {this.props.taskdata.data.imageTypes.length}
      <Button
        variant='light'
        data-test-cell-next
        disabled={this.state.hasNextPage}
        onClick={e => this.pageChange(+1)}>
        次のページ
      </Button>
    </div>
  }

  updateSentence(sentence) {
    const words = {}
    JSON.parse(this.props.taskdata.data.words).forEach(word => {
      if (word.filename !== this.state.currentFilename) {
        return
      }
      words[word.id] = word
    })
    if (sentence.fixedText !== true) {
      let r = ''
      sentence.words.forEach(word => {
        const text = words[word].text
        if (r.length > 0 && text.match(/^[A-Za-z0-9']+$/)) {
          r += ' '
        }
        r += text
      })
      sentence.text = r
    }
    if (sentence.fixedLocations !== true) {
      const locations = []
      sentence.words.forEach(word => {
        const { x1, y1, x2, y2 } = words[word].location
        if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) {
          return
        }
        locations.push(words[word].location)
      })
      sentence.locations = [{
        x1: Math.min(...locations.map(l => l.x1)),
        y1: Math.min(...locations.map(l => l.y1)),
        x2: Math.max(...locations.map(l => l.x2)),
        y2: Math.max(...locations.map(l => l.y2)),
      }]
    }
  }

  sentenceChanged(result) {
    console.log('Sentence changed', result)
    const { source, destination, draggableId } = result
    if (!destination) {
      return
    }
    const state = this.copyState()
    const sourceSentences = state.sentences.filter(s => s.id === source.droppableId)
    const destSentences = state.sentences.filter(s => s.id === destination.droppableId)
    if (source.droppableId === destination.droppableId) {
      const words = sourceSentences[0].words
      const [removed] = words.splice(source.index, 1)
      words.splice(destination.index, 0, removed)
      this.updateSentence(sourceSentences[0])
      state.changed = true
    } else if (sourceSentences.length > 0 && destSentences.length > 0) {
      const [removed] = sourceSentences[0].words.splice(source.index, 1)
      destSentences[0].words.splice(destination.index, 0, removed)
      this.updateSentence(sourceSentences[0])
      this.updateSentence(destSentences[0])
      state.changed = true
    } else if (sourceSentences.length > 0 && destination.droppableId.startsWith('NEW-')) {
      // Add
      const [removed] = sourceSentences[0].words.splice(source.index, 1)
      this.updateSentence(sourceSentences[0])
      const sourceIndices = state.sentences.map((s, index) => [s, index]).filter(s => s[0].id === source.droppableId)
      const sourceIndex = sourceIndices[0][1]
      const newSentence = {
        id: uuidv4(),
        color: randomColor(),
        words: [removed],
        filename: sourceSentences[0].filename,
      }
      this.updateSentence(newSentence)
      console.log('New data', newSentence)
      state.sentences.splice(sourceIndex + 1, 0, newSentence)
      state.changed = true
    } else if (sourceSentences.length > 0) {
      // Remove
      sourceSentences[0].words.splice(source.index, 1)
      this.updateSentence(sourceSentences[0])
      state.changed = true
    } else if (destSentences.length > 0) {
      // Restore
      destSentences[0].words.splice(destination.index, 0, draggableId)
      this.updateSentence(destSentences[0])
      state.changed = true
    } else if (destination.droppableId.startsWith('NEW-')) {
      // Add
      const sourceIndices = state.sentences.map((s, index) => [s, index]).filter(s => s[0].filename <= this.state.currentFilename)
      const sourceIndex = sourceIndices.length === 0 ? 0 : sourceIndices[sourceIndices.length - 1][1] + 1
      const newSentence = {
        id: uuidv4(),
        color: randomColor(),
        words: [draggableId],
        filename: this.state.currentFilename,
      }
      this.updateSentence(newSentence)
      state.sentences.splice(sourceIndex, 0, newSentence)
      state.changed = true
    }
    this.setState(state, () => this.drawDisplay())
  }

  saveChanges() {
    if (!this.props.onSaveChanges) {
      return
    }
    this.setState({
      updating: true,
    }, () => {
      this.props.onSaveChanges(this.state.sentences, () => this.setState({
        changed: false,
        updating: false,
      }))
    })
  }

  drawDisplay() {
    this.drawDisplay_()
    const canvas = this.displayRef.current
    this.setState({
      canvasURL: canvas.toDataURL(),
    })
  }

  drawDisplay_() {
    if (this.state.image === null) {
      return
    }
    const canvas = this.displayRef.current
    const image = this.imageRef.current

    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0);

    const baseSentences = this.state.sentences.filter(s => s.filename === this.state.currentFilename)
    const words = {}
    const remains = {}
    JSON.parse(this.props.taskdata.data.words).forEach(word => {
      if (word.filename !== this.state.currentFilename) {
        return
      }
      words[word.id] = word
      remains[word.id] = word
    })
    baseSentences.forEach(sentence => {
      (sentence.words || []).forEach(word => {
        remains[word] = undefined
      })
    })
    const disabledSentence = {
      id: `TRASH-${this.state.currentFilename}`,
      text: '表示しない',
      words: Object.entries(remains).filter(e => e[1] !== undefined).map(e => e[0])
    }
    const sentences = baseSentences.concat([disabledSentence])
    sentences.forEach((sentence, sentenceIndex) => {
      if (sentence.locations) {
        let minX = null
        let minY = null
        sentence.locations.forEach(location => {
          const { x1, y1, x2, y2 } = location
          if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) {
            return
          }
          ctx.strokeStyle = sentence.color
          ctx.lineWidth = 4
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1)
          minX = minX === null ? x1 : Math.min(minX, x1)
          minY = minY === null ? y1 : Math.min(minY, y1)
        })
        if (minX !== null && minY !== null) {
          ctx.fillStyle = sentence.color
          ctx.font = '48px Verdana'
          ctx.fillText(`#${sentenceIndex + 1}`, minX, minY - 4)
        }
      } else {
        (sentence.words || []).forEach(word => {
          const { x1, y1, x2, y2 } = words[word].location
          if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) {
            return
          }
          ctx.strokeStyle = '#aaa'
          ctx.lineWidth = 4
          ctx.strokeRect(x1, y1, x2 - x1, y2 - y1)
        })
      }
    })
  }

  imageLoad(event) {
    console.log('Loaded');
    const newState = {
      imageLoading: false,
    }
    const image = event.target
    if (image) {
      newState.imageSize = {
        width: image.width,
        height: image.height,
      }
    }
    this.setState(newState, () => this.drawDisplay())
  }

  resolveImageURL(image) {
    if (this.props.imageURL) {
      return this.props.imageURL(image);
    }
    return `${this.props.baseURL || ''}/image/${this.props.taskid}/${this.props.context}/${image}`;
  }

  moveSentence(sentenceId, to) {
    const state = this.copyState()
    const indices = state.sentences
      .map((sentence, index) => [sentence, index])
      .filter(s => s[0].id === sentenceId)
    if (indices.length === 0) {
      return
    }
    const sourceIndex = indices[0][1]
    const destIndex = sourceIndex + to
    if (state.sentences[destIndex] === undefined ||
      state.sentences[destIndex].filename !== state.sentences[sourceIndex].filename) {
      console.log('No changes')
      return
    }
    state.changed = true
    const [removed] = state.sentences.splice(sourceIndex, 1)
    state.sentences.splice(destIndex, 0, removed)
    this.setState(state, () => this.drawDisplay())
  }

  trashSentence(sentenceId) {
    const state = this.copyState()
    state.changed = true
    state.sentences = state.sentences.filter(s => s.id !== sentenceId)
    this.setState(state, () => this.drawDisplay())
  }

  renderEditor() {
    if (this.state.currentFilename === null || this.state.sentences === null) {
      return <div></div>
    }
    const baseSentences = this.state.sentences.filter(s => s.filename === this.state.currentFilename)
    const words = {}
    const remains = {}
    JSON.parse(this.props.taskdata.data.words).forEach(word => {
      if (word.filename !== this.state.currentFilename) {
        return
      }
      words[word.id] = word
      remains[word.id] = word
    })
    baseSentences.forEach(sentence => {
      (sentence.words || []).forEach(word => {
        remains[word] = undefined
      })
    })
    const newSentence = {
      dummy: true,
      id: `NEW-${this.state.currentFilename}`,
      text: '文として追加',
      words: [],
    }
    const disabledSentence = {
      dummy: true,
      id: `TRASH-${this.state.currentFilename}`,
      text: '表示しない',
      words: Object.entries(remains).filter(e => e[1] !== undefined).map(e => e[0])
    }
    const sentences = baseSentences.concat([newSentence, disabledSentence])

    return <div className='Sentences'>
      <DragDropContext onDragEnd={result => this.sentenceChanged(result)}>
        {sentences.map((sentence, sentenceIndex) =>
          <Droppable droppableId={sentence.id} key={sentence.id}>
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                className='DSentence'
                style={getListStyle(snapshot.isDraggingOver)}>
                {!sentence.dummy && <span className='DSentenceColor' style={{ backgroundColor: sentence.color }}>
                </span>}
                {!sentence.dummy && <span className='DSentenceIndex'>
                  #{sentenceIndex + 1}
                </span>}
                <span className='DSentenceText' onClick={() => {
                  if (!sentence.dummy) {
                    this.editSentence(sentence.id)
                  }
                }}>
                  {sentence.text}
                </span>
                {!sentence.dummy && <div className='DSentenceButtons'>
                  <Button
                    data-test-word-prev
                    onClick={e => this.moveSentence(sentence.id, -1)}
                    variant='light'
                    size='sm'>
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </Button>
                  <Button
                    data-test-word-next
                    onClick={e => this.moveSentence(sentence.id, +1)}
                    variant='light'
                    size='sm'>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Button>
                  <Button
                    data-test-word-trash
                    onClick={e => this.trashSentence(sentence.id, +1)}
                    variant='light'
                    size='sm'>
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </div>}
                {(sentence.words || []).map((word, wordIndex) =>
                  <Draggable draggableId={word} key={word} index={wordIndex}>
                    {(provided, snapshot) => (
                      <div
                        className='DWord'
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                            snapshot.isDragging,
                            provided.draggableProps.style
                        )}>
                          {words[word].text}
                      </div>
                    )}
                  </Draggable>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </DragDropContext>
    </div>
  }

  renderImage() {
    return <div className='ImageContainer'>
      <div>
        <ToggleButtonGroup
          type='radio'
          name='imageGuide'
          value={this.state.imageGuide}
          onChange={(imageGuide) => this.setState({ imageGuide })}>
          <ToggleButton value={true} variant='secondary'>領域表示</ToggleButton>
          <ToggleButton value={false} variant='secondary'>領域非表示</ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div>
        {this.state.canvasURL === null ? 'Loading...' : <Zoom>
            <img
              alt={`Display-${this.state.currentFilename}`}
              className={`Image ${this.state.imageGuide ? 'VisibleImage' : 'HiddenImage'}`}
              src={this.state.canvasURL}
            />
          </Zoom>}
        {this.state.currentFilename !== null && <Zoom>
            <img
              alt={`Current-${this.state.currentFilename}`}
              className={`CachedImage ${!this.state.imageGuide ? 'VisibleImage' : 'HiddenImage'}`}
              src={this.resolveImageURL(`${this.state.currentFilename}`)} />
          </Zoom>}
        <canvas
          className='HiddenImage'
          ref={this.displayRef}
          width={this.state.imageSize && this.state.imageSize.width}
          height={this.state.imageSize && this.state.imageSize.height}
        />
        {this.state.currentFilename !== null &&
          <img
            ref={this.imageRef}
            alt={`BaseImage-${this.state.currentFilename}`}
            className='CachedImage HiddenImage'
            onLoad={e => this.imageLoad(e)}
            src={this.resolveImageURL(`${this.state.currentFilename}`)}
          />}
        <div className='ImageEditorHelp'>
          画像をクリックすると拡大できます
        </div>
      </div>
    </div>
  }

  render() {
    return <div className='ImageEditor'>
      <h2>画像の編集</h2>
      {this.renderPageControl()}
      {this.renderImage()}
      {this.renderEditor()}
      {this.renderPageControl()}
      <div className='SaveChanges'>
        <Button
          data-test-task-save
          onClick={e => this.saveChanges(e)}
          disabled={this.state.updating || !this.state.changed}
          variant='success'>
          {this.state.updating ? '変更を反映中です...' : '変更を反映'}
        </Button>
      </div>
      {this.renderModal()}
    </div>
  }
}

export default ImageEditor;
